'use client';

/* ──────────────────────────────────────────────
 *  VoucherScanner — caméra du navigateur qui lit
 *  le QR d'une carte cadeau ou d'un lien promo
 * ────────────────────────────────────────────── */

import { useCallback, useState } from 'react';
import { parseBookingCodeScan } from '@cfixe-monorepo/shared';
import { useBarcodeScanner, type BarcodeScannerError } from '../../../shared/useBarcodeScanner';
import type { BookingLabels } from '../../types';

export type VoucherScannerLabels = Pick<
  BookingLabels,
  | 'voucher_scan_close'
  | 'voucher_scan_starting'
  | 'voucher_scan_scanning'
  | 'voucher_scan_unrecognized'
  | 'voucher_scan_gift_card_pdf'
  | 'voucher_scan_paused'
  | 'voucher_scan_resume'
  | 'voucher_scan_retry'
  | 'voucher_scan_error_denied'
  | 'voucher_scan_error_no_camera'
  | 'voucher_scan_error_busy'
  | 'voucher_scan_error_unavailable'
>;

/** `insecure` / `unsupported` n'arrivent pas ici : le bouton qui ouvre le scanner est alors masqué. */
const CAMERA_ERROR_LABELS: Record<BarcodeScannerError, keyof VoucherScannerLabels> = {
  insecure: 'voucher_scan_error_unavailable',
  unsupported: 'voucher_scan_error_unavailable',
  denied: 'voucher_scan_error_denied',
  no_camera: 'voucher_scan_error_no_camera',
  busy: 'voucher_scan_error_busy',
  decoder_unavailable: 'voucher_scan_error_unavailable',
  unknown: 'voucher_scan_error_unavailable',
};
/** Erreurs qu'un nouvel essai peut lever (autorisation rétablie, caméra libérée…). */
const RETRYABLE_CAMERA_ERRORS: BarcodeScannerError[] = ['denied', 'busy', 'decoder_unavailable', 'unknown'];

interface VoucherScannerProps {
  id: string;
  /** Code lu, déjà normalisé : l'appelant ferme le scanner et l'applique. */
  onCode: (code: string) => void;
  onClose: () => void;
  labels: VoucherScannerLabels;
}

/**
 * Monté = caméra demandée (le client vient de cliquer « Scanner »), démonté = caméra coupée. Un QR
 * qui ne porte pas de code laisse la lecture continuer avec un message, pour présenter le bon QR.
 */
export function VoucherScanner({ id, onCode, onClose, labels }: VoucherScannerProps) {
  const [feedback, setFeedback] = useState<string | undefined>(undefined);

  const handleDetected = useCallback(
    (rawValue: string): void => {
      const parsed = parseBookingCodeScan(rawValue);
      if (parsed.kind === 'code') {
        onCode(parsed.code);
        return;
      }
      setFeedback(
        parsed.kind === 'gift_card_pdf' ? labels.voucher_scan_gift_card_pdf : labels.voucher_scan_unrecognized,
      );
    },
    [onCode, labels.voucher_scan_gift_card_pdf, labels.voucher_scan_unrecognized],
  );

  const scanner = useBarcodeScanner({ active: true, onDetected: handleDetected });
  const isVideoVisible = scanner.status === 'starting' || scanner.status === 'scanning';
  const statusMessage =
    scanner.status === 'starting'
      ? labels.voucher_scan_starting
      : scanner.status === 'scanning'
        ? labels.voucher_scan_scanning
        : '';

  return (
    <div id={id} className="bt-voucher__scanner">
      {/* Masqué, jamais démonté : le scanner attache le flux à ce <video> dès que la caméra répond. */}
      <div
        className={
          isVideoVisible
            ? 'bt-voucher__camera ph-no-capture'
            : 'bt-voucher__camera bt-voucher__camera--hidden ph-no-capture'
        }
        aria-hidden="true"
      >
        <video ref={scanner.videoRef} className="bt-voucher__video" playsInline muted autoPlay />
        <div className="bt-voucher__camera-frame" />
      </div>

      <span className="bt-voucher__scan-status" aria-live="polite">
        {statusMessage}
      </span>

      {scanner.error ? (
        <div className="bt-voucher__scan-alert" role="alert">
          <span>{labels[CAMERA_ERROR_LABELS[scanner.error]]}</span>
          {RETRYABLE_CAMERA_ERRORS.includes(scanner.error) ? (
            <button type="button" className="bt-link-subtle" onClick={scanner.restart}>
              {labels.voucher_scan_retry}
            </button>
          ) : null}
        </div>
      ) : null}

      {scanner.status === 'paused' ? (
        <div className="bt-voucher__scan-alert bt-voucher__scan-alert--info">
          <span>{labels.voucher_scan_paused}</span>
          <button type="button" className="bt-link-subtle" onClick={scanner.restart}>
            {labels.voucher_scan_resume}
          </button>
        </div>
      ) : null}

      {feedback ? (
        <span className="bt-voucher__feedback bt-voucher__feedback--error" role="alert">
          {feedback}
        </span>
      ) : null}

      <button type="button" className="bt-link-subtle" onClick={onClose}>
        {labels.voucher_scan_close}
      </button>
    </div>
  );
}
